import { 
  Card, 
  CardFundingRequest, 
  CardFundingResponse,
  CardDispatchRequest,
  GetCardsParams,
  GetCardsResponse,
  TCreatePhysicalCardPayload,
  TCreatePhysicalCardResponse
} from '../types/cards';

// Use environment variable with fallback for development
const API_BASE_URL = import.meta.env.VITE_APP_API_URL || 'https://api-sandbox.useodysy.com';

export const createPhysicalCard = async (
  payload: TCreatePhysicalCardPayload
): Promise<TCreatePhysicalCardResponse> => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }

  console.log('Creating physical card with payload:', payload);
  console.log('Using API URL:', API_BASE_URL);

  const response = await fetch(`${API_BASE_URL}/admin/physical-cards`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `ClientBearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Card creation error:', error);
    throw new Error(error.message || 'Failed to create physical card');
  }

  return response.json();
};

export const fundCard = async (
  request: CardFundingRequest
): Promise<CardFundingResponse> => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }

  console.log('Funding card with request:', request);
  console.log('Using API URL:', API_BASE_URL);

  const response = await fetch(`${API_BASE_URL}/admin/card-fundings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `ClientBearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Card funding error:', error);
    throw new Error(error.message || 'Failed to fund card');
  }

  return response.json();
};

export const dispatchCard = async (
  request: CardDispatchRequest
): Promise<void> => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }
  const response = await fetch(`${API_BASE_URL}/admin/cards-dispatch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `ClientBearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Failed to dispatch card');
  }
};

export const getCards = async (
  params: GetCardsParams
): Promise<GetCardsResponse> => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.card_type) queryParams.append('card_type', params.card_type);
  if (params.next_cursor) queryParams.append('next_cursor', params.next_cursor);
  if (params.batch_id) queryParams.append('batch_id', params.batch_id);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);

  const response = await fetch(
    `${API_BASE_URL}/admin/cards?${queryParams.toString()}`,
    {
      headers: {
        'Authorization': `ClientBearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch cards');
  }

  return response.json();
};

export const getCardById = async (
  id: string,
  includeDispatchLog?: boolean,
  extraParams?: Record<string, string | number | boolean>
): Promise<Card> => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }
  const queryParams = new URLSearchParams();
  if (includeDispatchLog) queryParams.append('include_dispatch_log', '1');
  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      queryParams.append(key, String(value));
    });
  }

  const response = await fetch(
    `${API_BASE_URL}/admin/cards/${id}?${queryParams.toString()}`,
    {
      headers: {
        'Authorization': `ClientBearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch card');
  }

  return response.json();
};

export const getProductOrders = async (
  params: { next_cursor?: string; status?: string; include_dispatch_log?: boolean; dispatch_address_id?: string; sort_by?: string } = {}
): Promise<{ next_cursor?: string; product_orders: any[] }> => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }
  
  const queryParams = new URLSearchParams();
  if (params.next_cursor) queryParams.append('next_cursor', params.next_cursor);
  if (params.status) queryParams.append('status', params.status);
  if (params.include_dispatch_log) queryParams.append('include_dispatch_log', 'true');
  if (params.dispatch_address_id) queryParams.append('dispatch_address_id', params.dispatch_address_id);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);

  const response = await fetch(
    `${API_BASE_URL}/admin/card-batches?${queryParams.toString()}`,
    {
      headers: {
        'Authorization': `ClientBearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch product orders');
  }

  return response.json();
};

export const getProductOrderById = async (
  id: string,
  includeDispatchLog?: boolean,
  includeDestAddress?: boolean
): Promise<any> => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }
  const queryParams = new URLSearchParams();
  if (includeDispatchLog) queryParams.append('include_dispatch_log', '1');
  if (includeDestAddress) queryParams.append('include_dest_address', '1');

  const response = await fetch(
    `${API_BASE_URL}/admin/card-batches/${id}?${queryParams.toString()}`,
    {
      headers: {
        'Authorization': `ClientBearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch product order');
  }

  return response.json();
}; 

// Fetch pickup locations
export const getPickupLocations = async (params: { city?: string; state?: string; country?: string; include_all?: boolean; next_cursor?: string } = {}) => {
  const token = sessionStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication token not found');
  }

  const queryParams = new URLSearchParams();
  if (params.city) queryParams.append('city', params.city);
  if (params.state) queryParams.append('state', params.state);
  if (params.country) queryParams.append('country', params.country);
  if (params.include_all) queryParams.append('include_all', 'true');
  if (params.next_cursor) queryParams.append('next_cursor', params.next_cursor);

  const response = await fetch(
    `${API_BASE_URL}/admin/pickup-locations?${queryParams.toString()}`,
    {
      headers: {
        'Authorization': `ClientBearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch pickup locations');
  }

  return response.json();
}; 